/**
 * ENG-9781 — a synchronous subprocess call that is ACTUALLY bounded.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE BUG THIS EXISTS FOR
 * ─────────────────────────────────────────────────────────────────────────
 * `execFileSync(..., { timeout: 8_000 })` does not bound anything. Node fires
 * `killSignal` (SIGTERM by default) at the deadline and then KEEPS WAITING for
 * the child. There is no SIGKILL escalation, so a child that ignores or cannot
 * service SIGTERM blocks the calling thread for as long as it likes — and
 * `spawnSync` still reports `ETIMEDOUT`, so the error code tells you nothing
 * about how long you actually blocked.
 *
 * Measured on an agent host, not inferred:
 *
 *   spawnSync('sh', ['-c', "trap '' TERM; sleep 15"], { timeout: 2000 })
 *     → blocked 15003ms, error ETIMEDOUT
 *
 *   spawnSync('timeout', ['--signal=TERM','--kill-after=1s','2s', ...same...])
 *     → blocked  3004ms, signal SIGKILL
 *
 * On 2026-09-01 that gap took agt-aws-1 down twice. The manager-worker's
 * per-agent presence reaper calls `docker exec agt-<name> ps` synchronously; a
 * wedged dockerd meant the docker CLI never returned, `spawnSync` blocked the
 * event loop, and scheduling stopped for ALL 12 agents on the box for 33
 * minutes 46 seconds. The manager was not slow, it was gone. Same disease as
 * ENG-8079, where a per-agent `execFileSync` sweep made an
 * `AbortSignal.timeout(10_000)` fire 22-70s late on every attempt.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THE PROCESS GROUP MATTERS, AND WHY SIGKILL ON THE CHILD IS NOT ENOUGH
 * ─────────────────────────────────────────────────────────────────────────
 * `spawnSync` reads the child's stdout to EOF. EOF arrives when the last holder
 * of the write end closes it — which is NOT necessarily the direct child. Kill
 * only the child and a surviving grandchild keeps the pipe open, so the parent
 * stays blocked on a read even though the process it was waiting for is dead.
 *
 * GNU coreutils `timeout` runs the command in its own process group and signals
 * the GROUP (that is precisely what `--foreground` turns off), so descendants go
 * with it and the pipe closes. That is why this wraps rather than reimplementing
 * the escalation in JS: `spawnSync` gives us no handle to signal a group with.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHAT IT REFUSES TO DO: PRETEND
 * ─────────────────────────────────────────────────────────────────────────
 * On a host with no `timeout` binary the call still runs, but the result is
 * marked `bounded: false` and the absence is logged ONCE per process — through
 * `console.warn` when the call site passed no logger, because a one-shot latch
 * consumed without emitting anything is the same silence with extra steps.
 * Silently degrading to an unbounded call while the call site believes it is
 * protected is the same failure this module exists to fix, one level up: a guard
 * you cannot verify is a claim, not a control.
 *
 * The same refusal applies to its own configuration. A duration of zero (or one
 * that rounds to zero) is how GNU `timeout` spells "disable", so a caller that
 * passed one would get an unbounded call reporting `bounded: true`. Those are
 * rejected outright rather than clamped: see MIN_DURATION_MS.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * TRI-STATE, DELIBERATELY
 * ─────────────────────────────────────────────────────────────────────────
 * `ok` / `timed-out` / `failed` are three different things and callers act on
 * them differently. Wrapping introduces three statuses whose owner is ambiguous
 * — 124, 126/127 — and they are NOT resolved the same way, because they are not
 * the same kind of thing:
 *
 *   124      an ordinary exit status a command may own. `timeout` writes
 *            nothing on its deadline, so the status cannot say who spoke —
 *            but ELAPSED TIME can, exactly (the wrapper cannot fire before
 *            `timeoutMs`). Discriminated.
 *   137      not an exit status at all: 128+9, a SIGKILLed process. Always
 *            read as no-answer, with no elapsed check — see the comment at
 *            the branch for why the fast-OOM case makes that the true reading
 *            as well as the safe one.
 *   126/127  `timeout`'s own launch failures, reusing statuses docker really
 *            returns. Discriminated on whether `timeout` announced itself on
 *            stderr, defaulting to the COMMAND's meaning. In particular `timed-out` means WE HAVE NO MEASUREMENT — it
 * must never be read as evidence about the thing being measured. The presence
 * reapers restart sessions on what this returns; turning "docker did not answer"
 * into "every declared MCP server is missing" would convert a docker hiccup into
 * a fleet-wide restart storm.
 */
import { execFileSync } from 'node:child_process';

/** Exit status GNU `timeout` uses when it had to signal the command. */
const TIMEOUT_EXIT_DEADLINE = 124;
/** Exit status seen when the command was SIGKILLed (128 + 9). */
const TIMEOUT_EXIT_KILLED = 137;
/** Exit status GNU `timeout` uses when the command could not be run at all. */
const TIMEOUT_EXIT_CANNOT_RUN = 126;
/** Exit status GNU `timeout` uses when the command was not found. */
const TIMEOUT_EXIT_NOT_FOUND = 127;

/** How long after the TERM deadline before SIGKILL. */
export const DEFAULT_KILL_AFTER_MS = 2_000;

/**
 * Smallest duration this module will hand to `timeout`, and the floor is 1ms
 * rather than "greater than zero" for a reason that is easy to get wrong.
 *
 * GNU `timeout` treats a duration of ZERO as "disable" — a `0` main duration
 * sends no signal at all, and `--kill-after=0` disables the SIGKILL escalation
 * even when the main deadline is live. Either one turns this call back into the
 * unbounded `execFileSync` this module exists to replace, while it still
 * reports `bounded: true`. That is not a slow guard, it is a guard that lies.
 *
 * And `> 0` does not close it: `toTimeoutArg` renders with `toFixed(3)`, so
 * anything under a millisecond — `0.4`, say — is a positive, finite number that
 * still comes out as the literal string `"0.000s"`. The floor has to be at the
 * rendering's precision, not at zero.
 *
 * The unwrapped path needs the same rule: Node reads `timeout: 0` as "no
 * timeout" too, so validation happens BEFORE the availability probe and applies
 * to both paths identically.
 */
const MIN_DURATION_MS = 1;

export type BoundedExecResult =
  | { kind: 'ok'; stdout: string; bounded: boolean }
  /** The deadline fired. We have NO measurement — never read this as a negative. */
  | { kind: 'timed-out'; bounded: boolean }
  /** The command ran and answered with a non-zero status (a real answer). */
  | {
      kind: 'failed';
      status: number | null;
      message: string;
      bounded: boolean;
      /**
       * The binary itself could not be found. Surfaced as a flag rather than
       * left for call sites to sniff out of a message, because the wrapper
       * changes how it presents: unwrapped it is `ENOENT`, wrapped it is
       * `timeout` exiting 127. A call site that has to string-match one of
       * those will quietly stop working the day the other one is in play —
       * and "docker is not installed" vs "docker is wedged" is a distinction
       * the isolation preflight makes a whole-fleet decision on.
       */
      notFound?: boolean;
    };

export interface BoundedExecOptions {
  /** Wall-clock deadline for the command itself, before escalation. */
  timeoutMs: number;
  /** Grace between SIGTERM and SIGKILL. Total bound is timeoutMs + killAfterMs. */
  killAfterMs?: number;
  maxBuffer?: number;
  /** Injection seam for tests. Defaults to node:child_process execFileSync. */
  exec?: typeof execFileSync;
  /** Injection seam for tests: whether coreutils `timeout` is available. */
  timeoutBinaryAvailable?: () => boolean;
  /**
   * Injection seam for tests: a MONOTONIC clock in milliseconds.
   *
   * Monotonic, not `Date.now()`, and that is load-bearing rather than
   * fastidious. The elapsed reading below decides whether a `124` came from
   * our own deadline or from the command, and a wall clock is NTP-steppable:
   * a backward step mid-call would make a genuine wrapper timeout read as
   * "returned early", hand a non-null status to `livenessFromProbe`, and
   * authorise a reap on a probe that never answered. That is the exact wrong
   * answer this module exists to refuse.
   */
  now?: () => number;
  /** Where the one-shot "no timeout binary" warning goes. */
  log?: (msg: string) => void;
}

/** Memoised probe for the `timeout` binary; also the one-shot warning latch. */
let timeoutBinaryProbe: boolean | null = null;
let warnedNoTimeoutBinary = false;
/** One-shot latch for the invalid-duration refusal. Separate on purpose: the
 *  two conditions are independent, and sharing a latch would let an
 *  environment problem silence a code bug or the reverse. */
let warnedInvalidDuration = false;

/** Test seam — resets the memoised probe and the one-shot warning latches. */
export function __resetBoundedExecProbeForTests(): void {
  timeoutBinaryProbe = null;
  warnedNoTimeoutBinary = false;
  warnedInvalidDuration = false;
}

/**
 * Test seam — pin the memoised availability answer without shelling out.
 *
 * For suites that mock the child_process seam and count calls: without this,
 * the one-off `timeout --version` probe consumes one of their mocked calls, and
 * WHICH test pays for it depends on execution order. Pin it and the probe is
 * invisible to them; its own behaviour is covered directly in
 * eng-9781-bounded-exec.test.ts.
 */
export function __setBoundedExecProbeForTests(available: boolean): void {
  timeoutBinaryProbe = available;
  warnedNoTimeoutBinary = false;
  warnedInvalidDuration = false;
}

export function hasTimeoutBinary(exec: typeof execFileSync): boolean {
  if (timeoutBinaryProbe !== null) return timeoutBinaryProbe;
  try {
    // `timeout --version` rather than `which timeout`: it proves the binary both
    // exists AND is executable by this uid, in one call, and it is the GNU
    // implementation we are relying on for the group-kill semantics.
    exec('timeout', ['--version'], { timeout: 5_000, stdio: ['ignore', 'pipe', 'ignore'] });
    timeoutBinaryProbe = true;
  } catch {
    timeoutBinaryProbe = false;
  }
  return timeoutBinaryProbe;
}

/** Seconds string GNU `timeout` accepts, with millisecond precision preserved. */
function toTimeoutArg(ms: number): string {
  return `${(ms / 1000).toFixed(3)}s`;
}

/**
 * Reject a duration that would silently disable the thing it configures.
 * Returns the operator-facing reason, or null when the value is usable.
 */
function invalidDuration(name: string, ms: number): string | null {
  if (Number.isFinite(ms) && ms >= MIN_DURATION_MS) return null;
  return (
    `[bounded-exec] refusing to run: ${name}=${String(ms)} is not a usable deadline ` +
    `(needs a finite value >= ${MIN_DURATION_MS}ms). A zero or sub-millisecond duration ` +
    `DISABLES the deadline or the SIGKILL escalation, which would leave this call ` +
    `unbounded while reporting that it was bounded (ENG-9781).`
  );
}

/**
 * The wrapped argv that IS the bound.
 *
 * Extracted so an async caller can obtain the identical `timeout --signal=TERM
 * --kill-after=... <ms> <file> <args>` construction without re-deriving it.
 * Sharing this rather than copying it is the point: the bound lives in these
 * arguments, and a second hand-written copy would drift from the one the sync
 * path is tested against.
 *
 * It does NOT decide whether `timeout` is available — the caller passes that,
 * because the probe itself runs a subprocess and an async caller must not be
 * forced through a synchronous one to find out.
 */
export function boundedArgv(
  file: string,
  args: readonly string[],
  timeoutMs: number,
  killAfterMs: number,
  available: boolean,
): { execFile: string; execArgs: string[] } {
  return available
    ? {
        execFile: 'timeout',
        execArgs: [
          '--signal=TERM',
          `--kill-after=${toTimeoutArg(killAfterMs)}`,
          toTimeoutArg(timeoutMs),
          file,
          ...args,
        ],
      }
    : { execFile: file, execArgs: [...args] };
}

/**
 * Run a command with a bound that actually holds.
 *
 * Total worst case is `timeoutMs + killAfterMs` when the `timeout` binary is
 * present, because SIGKILL cannot be ignored and the whole process group goes
 * with it. Without the binary there is no bound and `bounded: false` says so.
 *
 * Never throws: every outcome is a value, because the point of the module is
 * that callers on the manager's critical path handle "no answer" explicitly
 * rather than through a catch that also swallows real answers.
 */
export function hardBoundedExecFile(
  file: string,
  args: readonly string[],
  opts: BoundedExecOptions,
): BoundedExecResult {
  const exec = opts.exec ?? execFileSync;
  const killAfterMs = opts.killAfterMs ?? DEFAULT_KILL_AFTER_MS;
  const log = opts.log;

  // Before anything else, and before the availability probe: a duration that
  // disables its own deadline must never reach either path. See
  // MIN_DURATION_MS. `status: null` is deliberate — it is not a status the
  // command produced, and it must not collide with the real statuses call
  // sites branch on (persistent-session reads docker's 1/125/126 as
  // "confidently dead"; a null lands in its fail-safe ALIVE branch, which is
  // the right place for "we never ran the command").
  const badDuration =
    invalidDuration('timeoutMs', opts.timeoutMs) ?? invalidDuration('killAfterMs', killAfterMs);
  if (badDuration) {
    // Latched like the unbounded warning above, and for the same reason: these
    // durations are compile-time literals at every call site, so a bad one is a
    // code defect that would otherwise repeat per agent per poll cycle, and a
    // guard that floods the log is a guard people filter out. It is not lost
    // in the meantime — the reason rides back on `message`, which every call
    // site already surfaces. Only `hardBoundedCapture` collapses it to null,
    // which is exactly why this is logged at all rather than left to callers.
    if (!warnedInvalidDuration) {
      warnedInvalidDuration = true;
      (log ?? ((m: string) => console.warn(m)))(badDuration);
    }
    return { kind: 'failed', status: null, message: badDuration, bounded: false };
  }

  const available = opts.timeoutBinaryAvailable
    ? opts.timeoutBinaryAvailable()
    : hasTimeoutBinary(exec);

  if (!available && !warnedNoTimeoutBinary) {
    warnedNoTimeoutBinary = true;
    // `log ?? console.warn`, NOT `log?.()`. The latch is one-shot per PROCESS,
    // so a call with no logger used to consume the only warning this module
    // will ever emit and print nothing — and then suppress every later call
    // that did have one.
    //
    // That is not a hypothetical ordering. The loggerless callers are
    // `persistent-session`'s isolation preflight (`docker info`, `docker image
    // inspect`), which runs ONCE PER PROCESS at boot, before any reaper has a
    // chance to call in with a logger. On a host with no `timeout` binary the
    // warning was therefore all but guaranteed to be swallowed — leaving the
    // manager running unbounded docker calls with nothing in the log saying so,
    // which is precisely the silent degradation the header refuses to do.
    (log ?? ((m: string) => console.warn(m)))(
      `[bounded-exec] coreutils 'timeout' not found — subprocess calls on the ` +
        `manager's critical path are running UNBOUNDED. A wedged '${file}' can ` +
        `block the event loop for every agent on this host (ENG-9781).`,
    );
  }

  // The Node-level `timeout` is kept even when wrapped. It is not the bound —
  // it cannot be — but it is a second, independent deadline, and it costs
  // nothing. Given a slack over the wrapper's own escalation so the wrapper is
  // what fires in the normal case and this only matters if `timeout` itself
  // wedges, which would otherwise be an unbounded hole in the guard.
  const nodeTimeoutMs = available ? opts.timeoutMs + killAfterMs + 5_000 : opts.timeoutMs;

  const { execFile, execArgs } = boundedArgv(file, args, opts.timeoutMs, killAfterMs, available);

  const now = opts.now ?? (() => performance.now());
  const startedAt = now();

  try {
    const stdout = exec(execFile, execArgs, {
      encoding: 'utf-8',
      timeout: nodeTimeoutMs,
      maxBuffer: opts.maxBuffer,
      // stderr is CAPTURED rather than discarded: it is the only place the
      // command says why it failed, and a reaper that logs "docker exec failed"
      // with no reason is the kind of line that costs an hour at 3am.
      stdio: ['ignore', 'pipe', 'pipe'],
    }) as unknown as string;
    return { kind: 'ok', stdout: String(stdout ?? ''), bounded: available };
  } catch (err) {
    const e = err as NodeJS.ErrnoException & {
      status?: number | null;
      signal?: NodeJS.Signals | null;
    };
    const status = e?.status ?? null;
    const elapsedMs = now() - startedAt;

    // 137 is 128+9: the command was SIGKILLed. A signalled process has no exit
    // status of its own, so "no answer" is not a lossy collapse here, it is
    // the accurate reading — whoever fired the signal. NO elapsed check, and
    // that refusal is deliberate.
    //
    // `docker exec` returns 128+N when the exec'd process is signalled, so an
    // in-container probe killed by the OOM killer surfaces as a FAST 137 that
    // elapsed time cannot separate from a command choosing to `exit 137`. And
    // the consumer makes the direction matter: `livenessFromProbe`
    // (stale-mcp-reaper.ts:68) reads ANY non-null `failed` status as DEAD, and
    // that verdict authorises a reap. Preserving a 137 there would convert "our
    // probe was killed, we learned nothing" into "the MCP child is dead, reap
    // it" — on a fleet with live memory pressure (ENG-9702). The safe reading
    // wins, because it is also the true one.
    if (available && status === TIMEOUT_EXIT_KILLED) {
      return { kind: 'timed-out', bounded: true };
    }

    // 124 is different in kind, and gets a discriminator rather than a rule.
    // It is an ordinary exit status a command may legitimately own, and GNU
    // `timeout` writes NOTHING to stderr when it fires — so unlike the 126/127
    // case below, the status alone cannot say who spoke.
    //
    // Elapsed time can, exactly: the wrapper cannot signal before `timeoutMs`,
    // so anything that came back sooner is the command's own answer. Parent-
    // observed elapsed is always >= the child's runtime (spawn overhead only
    // adds), which is why this needs no epsilon.
    //
    // The safety argument for letting a real 124 through to `failed` — and so
    // to `livenessFromProbe`'s DEAD branch — is that no command this module
    // wraps can emit one: `ps`, `pgrep`, `kill` and the docker CLI have no 124
    // in their vocabularies. Re-check that when adding a call site.
    if (available && status === TIMEOUT_EXIT_DEADLINE && elapsedMs >= opts.timeoutMs) {
      return { kind: 'timed-out', bounded: true };
    }
    // Node's own deadline, or a kill from anywhere else: also no answer.
    if (e?.code === 'ETIMEDOUT' || e?.signal === 'SIGTERM' || e?.signal === 'SIGKILL') {
      return { kind: 'timed-out', bounded: available };
    }
    const stderr = String((e as { stderr?: unknown })?.stderr ?? '').trim();

    // THE SECOND AMBIGUITY THE WRAPPER INTRODUCES (see 124 above for the
    // first), and it is resolved in the direction that preserves the caller's
    // meaning.
    //
    // GNU `timeout` reuses 126/127 for its OWN launch failures — the same
    // statuses a command may legitimately exit with. `persistent-session`
    // branches on docker's 125/126 to mean "container gone → confidently dead",
    // and docker returning 126 at runtime is common, so swallowing those into
    // "no status" would break crash detection for every isolated agent. A test
    // caught exactly that on the first run of this module.
    //
    // So the command's status is passed through by DEFAULT, and only a stderr
    // line that `timeout` itself wrote ("timeout: failed to run command ...")
    // is treated as the wrapper speaking. Best-effort by construction, and when
    // it is unsure it prefers the command's meaning over the wrapper's — the
    // safe direction, because misreading a real 126 is the expensive mistake
    // and misreading a wrapper 126 only costs us a slightly wrong log line on a
    // host where the binary is already unusable.
    if (
      available &&
      (status === TIMEOUT_EXIT_CANNOT_RUN || status === TIMEOUT_EXIT_NOT_FOUND) &&
      /^timeout:/m.test(stderr)
    ) {
      return {
        kind: 'failed',
        status: null,
        message: `timeout could not run '${file}' (wrapper exit ${status}): ${stderr}`,
        bounded: true,
        notFound: status === TIMEOUT_EXIT_NOT_FOUND,
      };
    }
    const base = (e as Error)?.message ?? String(err);
    return {
      kind: 'failed',
      status,
      message: stderr ? `${base}: ${stderr}` : base,
      bounded: available,
      notFound: e?.code === 'ENOENT',
    };
  }
}

/**
 * Convenience for the overwhelmingly common shape at these call sites: "give me
 * the stdout, or nothing". Collapses `timed-out` and `failed` to `null` for
 * callers that genuinely treat both the same — but the tri-state above is the
 * one to reach for when they do not, and the reapers do not.
 */
export function hardBoundedCapture(
  file: string,
  args: readonly string[],
  opts: BoundedExecOptions,
): string | null {
  const r = hardBoundedExecFile(file, args, opts);
  return r.kind === 'ok' ? r.stdout : null;
}
